import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TotpInvalidError, TotpRequiredError, useAuth } from "../lib/auth";
import BriefcaseLogo from "../components/BriefcaseLogo";

export default function Login() {
  const navigate = useNavigate();
  const login = useAuth((s) => s.login);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email, password, needsTotp ? totp : undefined);
      navigate("/", { replace: true });
    } catch (e) {
      if (e instanceof TotpRequiredError) {
        setNeedsTotp(true);
        setErr(null);
      } else if (e instanceof TotpInvalidError) {
        setErr("That code didn't match. Try the next one from your app.");
        setTotp("");
      } else {
        setErr((e as Error)?.message ?? "Login failed");
        // If credentials changed, fall back to password step.
        setNeedsTotp(false);
        setTotp("");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={onSubmit}>
        <div className="login__brand">
          <span className="shell__logo" aria-hidden>
            <BriefcaseLogo size={22} />
          </span>
        </div>
        <h1>{needsTotp ? "Verification code" : "Sign in"}</h1>

        {!needsTotp ? (
          <>
            <label>
              <span>Email</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Enter the 6-digit code from your authenticator app for{" "}
              <strong>{email}</strong>.
            </p>
            <label>
              <span>Authentication code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={8}
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                autoFocus
                required
              />
            </label>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => { setNeedsTotp(false); setTotp(""); setErr(null); }}
            >
              ← Back
            </button>
          </>
        )}

        {err && <div className="alert alert--error">{err}</div>}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={busy || (needsTotp && totp.length < 6)}
        >
          {busy ? "Signing in…" : needsTotp ? "Verify" : "Sign in"}
        </button>


      </form>
    </div>
  );
}
